/** The part of noVNC's RFB client the remote support page uses (noVNC `docs/API.md`). */
export interface NoVncRfb extends EventTarget {
  scaleViewport: boolean;
  focusOnClick: boolean;
  qualityLevel: number;
  compressionLevel: number;
  viewOnly: boolean;
  disconnect(): void;
}

/** The client message encoders noVNC 1.7.0 keeps in `RFB.messages`, for those it has no API for. */
export interface NoVncMessages {
  setDesktopSize(
    sock: unknown,
    width: number,
    height: number,
    id: number,
    flags: number,
  ): void;
}

/** noVNC takes a WebSocket URL or a raw channel: here, the terminal's `vnc` data channel. */
export type NoVncRfbConstructor = (new (
  target: HTMLElement,
  channel: object,
  options?: { shared?: boolean },
) => NoVncRfb) & { messages: NoVncMessages };

/**
 * Where angular.json copies noVNC's ES modules. They load natively, outside the bundle: noVNC
 * 1.5+ uses top-level await, which the Angular webpack build turns off.
 */
const RFB_MODULE_PATH = 'assets/novnc/core/rfb.js';

/** The WebCodecs decoder noVNC probes for H.264 while its modules load. */
const VIDEO_DECODER = 'VideoDecoder';

let rfbModule: Promise<NoVncRfbConstructor> | null = null;

/** Loads noVNC once per page; a failed load is tried again on the next call. */
export function loadNoVnc(): Promise<NoVncRfbConstructor> {
  if (!rfbModule) {
    const url = new URL(RFB_MODULE_PATH, document.baseURI).href;
    rfbModule = withoutVideoDecoder(() =>
      import(/* webpackIgnore: true */ url).then(
        (module: { default: NoVncRfbConstructor }) => module.default,
      ),
    );
    rfbModule.catch(() => {
      rfbModule = null;
    });
  }
  return rfbModule;
}

/**
 * Runs `load` with `window.VideoDecoder` hidden, and puts it back once the load settles.
 *
 * @remarks
 * While its modules load, noVNC awaits a test decode of one H.264 frame (`core/util/browser.js`),
 * and the import does not finish until that decode does. Where the browser never completes it
 * (headless Chrome, a tab whose decoders are reclaimed) the screen never opens. The terminal only
 * sends Tight, never H.264, so noVNC loses nothing when the probe finds no decoder.
 * Nothing else in isoFarm uses WebCodecs.
 */
function withoutVideoDecoder<T>(load: () => Promise<T>): Promise<T> {
  const descriptor = Object.getOwnPropertyDescriptor(window, VIDEO_DECODER);
  if (!descriptor?.configurable) {
    return load();
  }
  Reflect.deleteProperty(window, VIDEO_DECODER);
  return load().finally(() =>
    Object.defineProperty(window, VIDEO_DECODER, descriptor),
  );
}
